// src/lib/collectionNotes.js
// Private per-card collector notes — the single access point for the
// public.collection_notes table.
//
// These notes live in their OWN table rather than as a column on
// public.collections, and that is load-bearing, not stylistic. collections
// carries two broad public SELECT policies (public profiles + marketplace
// listings) and RLS is row-level, so every column of a public-profile
// collector's row is readable through the REST API with the anon key that
// ships in the frontend bundle. A notes column there would be public by
// construction. collection_notes has no public policy at all.
//
// Keyed by card_key, exactly as collections is, so a note belongs to one
// specific copy of a card (a serial-numbered parallel is its own entry, and
// therefore its own note).

import { supabase } from '../supabaseClient';

export const NOTE_MAX = 500;

/** Read the note for one entry. Returns '' when there is none. */
export async function fetchNote(userId, cardKey) {
  if (!userId || !cardKey) return '';
  const { data, error } = await supabase
    .from('collection_notes')
    .select('note')
    .eq('user_id', userId)
    .eq('card_key', cardKey)
    .maybeSingle();
  if (error) {
    console.warn('[collectionNotes] fetchNote failed:', error.message);
    return '';
  }
  return data?.note || '';
}

/**
 * Write or clear the note for one entry.
 * An empty/whitespace-only note deletes the row rather than storing '' — the
 * table's job is to answer "does this entry have a note?", and a blank row
 * would wrongly light up the checklist marker.
 */
export async function saveNote(userId, cardKey, note) {
  if (!userId || !cardKey) return;
  const trimmed = (note || '').trim();

  if (trimmed === '') {
    const { error } = await supabase
      .from('collection_notes')
      .delete()
      .eq('user_id', userId)
      .eq('card_key', cardKey);
    if (error) throw new Error('Note delete failed: ' + error.message);
    return;
  }

  const { error } = await supabase
    .from('collection_notes')
    .upsert(
      { user_id: userId, card_key: cardKey, note: trimmed.slice(0, NOTE_MAX) },
      { onConflict: 'user_id,card_key' }
    );
  if (error) throw new Error('Note save failed: ' + error.message);
}

/**
 * Every card_key in this set that the collector has a note on.
 * Returns a Set for O(1) lookup while rendering rows. The prefix scan rides
 * the (user_id, card_key) primary key. Notes themselves are NOT fetched —
 * the checklist only needs to know which rows carry one, and not pulling the
 * prose keeps it off a page a collector may be screen-sharing.
 */
export async function fetchNoteKeysForSet(userId, setId) {
  if (!userId || !setId) return new Set();
  const { data, error } = await supabase
    .from('collection_notes')
    .select('card_key')
    .eq('user_id', userId)
    .like('card_key', `${setId}__%`);
  if (error) {
    console.warn('[collectionNotes] fetchNoteKeysForSet failed:', error.message);
    return new Set();
  }
  return new Set((data || []).map(r => r.card_key));
}

/**
 * Bulk write for the import: `entries` is [{ card_key, note }].
 * Blank notes are collected into one delete rather than a row of empty strings,
 * matching saveNote's single-entry behaviour. Chunked because PostgREST has a
 * request-size ceiling and an import can carry thousands of rows.
 */
export async function saveNotesBulk(userId, entries, chunkSize = 500) {
  if (!userId || !entries || entries.length === 0) return;

  const writes = [];
  const clears = [];
  for (const { card_key: cardKey, note } of entries) {
    if (!cardKey) continue;
    const trimmed = (note || '').trim();
    if (trimmed === '') clears.push(cardKey);
    else writes.push({ user_id: userId, card_key: cardKey, note: trimmed.slice(0, NOTE_MAX) });
  }

  for (let i = 0; i < writes.length; i += chunkSize) {
    const { error } = await supabase
      .from('collection_notes')
      .upsert(writes.slice(i, i + chunkSize), { onConflict: 'user_id,card_key' });
    if (error) throw new Error('Note import failed: ' + error.message);
  }
  for (let i = 0; i < clears.length; i += chunkSize) {
    const { error } = await supabase
      .from('collection_notes')
      .delete()
      .eq('user_id', userId)
      .in('card_key', clears.slice(i, i + chunkSize));
    if (error) throw new Error('Note import failed: ' + error.message);
  }
}

/** All of a collector's notes, as a { card_key: note } map — used by the export. */
export async function fetchAllNotes(userId) {
  if (!userId) return {};
  const { data, error } = await supabase
    .from('collection_notes')
    .select('card_key, note')
    .eq('user_id', userId);
  if (error) {
    console.warn('[collectionNotes] fetchAllNotes failed:', error.message);
    return {};
  }
  const map = {};
  (data || []).forEach(r => { map[r.card_key] = r.note; });
  return map;
}
